import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';

import {FormInput} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {CustomValidatedControl} from 'src/contexts/validation-context';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {BASIC_VALIDATION_FAILED} from 'src/modules/create-account/constants';
import {PresetEntitlementsTable, PresetSuggestSection} from 'src/modules/manage-entitlements/presets-entitlements';
import {useAssignGrantsToPreset} from 'src/modules/manage-entitlements/presets-entitlements/useAssignGrantsToPreset';
import {useAssignRolesToPreset} from 'src/modules/manage-entitlements/presets-entitlements/useAssignRolesToPreset';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import Button from '@pb/kit-component-button';
import {Dialog, DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {FormSection} from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import Loading from '@pb/kit-component-loading';
import {Message} from '@pb/kit-component-message';
import {Textarea} from '@pb/kit-component-textarea-v2';
import {useShown} from '@pb/kit-hooks-use-shown';

import {ManageEntitlementsPage} from '../components/manage-entitlements-page';

import {PRESET_NAME_CONTROL} from './constants';
import {REMOVE_PRESET_MODAL_WIDTH, RemovePresetModal} from './remove-preset-modal';
import {useDischargeGrantsFromPreset} from './useDischargeGrantsFromPreset';
import {useDischargeRolesFromPreset} from './useDischargeRolesFromPreset';
import {useEditPreset} from './useEditPreset';
import {useEditRelations} from './useEditRelations';

import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {PresetRelation} from 'src/openapi/admin/models/PresetRelation';
import type {PresetRelationItem} from 'src/openapi/admin/models/PresetRelationItem';


const EditPreset = () => {
    const queryParams = useParams<{presetId: string}>();
    const presetId = queryParams.presetId || '';

    const {shown: saveShown, onClick: openSave, onClose: closeSave} = useShown();
    const {shown: deleteShown, onClick: openDelete, onClose: closeDelete} = useShown();

    const navigate = useNavigate();
    const {hasAccess} = useAdminAccessRight();
    const {setSuccessMessage, setErrorMessage} = useContext(MessageContext);
    const {proceed, setIsChunkChanged, isCurrentChunkChanged} = useConfirmationChunk({chunkName: 'edit-preset', onProceed: () => {
        closeSave();
        setSuccessMessage('Preset has been successfully updated.');
    }});

    const [busy, setBusy] = useState(false);
    const [reason, setReason] = useState('');
    const [errorMessage, setFormErrorMessage] = useState('');

    const {
        isLoading: isPresetLoading,
        loadingError: presetLoadingError,
        isChanged: isPresetChanged,
        data: preset,
        setData: setPreset,
        saveChanges: savePreset,
        discardChanges: discardPreset,
        validateChanges: validatePresetFields,
    } = useEditPreset({presetId});

    const {
        isLoading: isPresetRelationsLoading,
        loadingError: presetRelationsLoadingError,
        isChanged: isPresetRelationsChanged,
        data: relations,
        dataInitial: initialRelations,
        setData: setRelations,
        saveChanges: savePresetRelations,
        discardChanges: discardPresetRelations,
        validateChanges: validatePresetRelations,
    } = useEditRelations({presetId});

    const rolesToAdd = useMemo(() => {
        return relations
            .filter(rel => !initialRelations.some(initRel => isDeepEqual({
                prev: initRel,
                current: rel,
            })))
            .filter(({roleId, level}) => roleId && level)
            .map(({roleId, level}) => ({entityId: roleId || '', relationLevel: level}))
            .reduce((acc, {relationLevel, entityId}) => {
                const existed = acc.find(({entityId: id}) => id === entityId);

                if (existed) {
                    return acc;
                }

                return [...acc, {entityId, relationLevel}];
            }, [] as PresetRelation[]);
    }, [relations, initialRelations]);

    const rolesToDischarge = useMemo(() => {
        return initialRelations
            .filter(rel => !relations.some(initRel => isDeepEqual({
                prev: initRel,
                current: rel,
            })))
            .filter(({roleId, level}) => roleId && level)
            .map(({roleId, level}) => ({entityId: roleId || '', relationLevel: level}))
            .reduce((acc, {entityId}) => {
                const existed = acc.find(id => id === entityId);

                if (existed) {
                    return acc;
                }

                return [...acc, entityId];
            }, [] as string[]);
    }, [relations, initialRelations]);

    const grantsToAdd = useMemo(() => {
        return relations
            .filter(rel => !initialRelations.some(initRel => isDeepEqual({
                prev: initRel,
                current: rel,
            })))
            .filter(({roleId, grantId, level}) => !roleId && grantId && level)
            .map(({grantId, level}) => ({entityId: grantId || '', relationLevel: level}))
            .reduce((acc, {relationLevel, entityId}) => {
                const existed = acc.find(({entityId: id}) => id === entityId);

                if (existed) {
                    return acc;
                }

                return [...acc, {entityId, relationLevel}];
            }, [] as PresetRelation[]);
    }, [relations, initialRelations]);

    const grantsToDischarge = useMemo(() => {
        return initialRelations
            .filter(rel => !relations.some(initRel => isDeepEqual({
                prev: initRel,
                current: rel,
            })))
            .filter(({roleId, grantId, level}) => !roleId && grantId && level)
            .map(({grantId, level}) => ({entityId: grantId || '', relationLevel: level}))
            .reduce((acc, {entityId}) => {
                const existed = acc.find(id => id === entityId);

                if (existed) {
                    return acc;
                }

                return [...acc, entityId];
            }, [] as string[]);
    }, [relations, initialRelations]);

    const {event: assignRolesToPreset} = useAssignRolesToPreset(rolesToAdd);
    const {event: assignGrantsToPreset} = useAssignGrantsToPreset(grantsToAdd);
    const {event: dischargeRolesFromPreset} = useDischargeRolesFromPreset(rolesToDischarge);
    const {event: dischargeGrantsFromPreset} = useDischargeGrantsFromPreset(grantsToDischarge);

    useEffect(() => {
        setIsChunkChanged(isPresetChanged || isPresetRelationsChanged);
    }, [isPresetChanged, isPresetRelationsChanged, setIsChunkChanged]);

    const changeName = useCallback((name: string) => {
        setPreset(prev => ({...prev, name}));
    }, [setPreset]);

    const changeDescription = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setPreset(prev => ({...prev, description: event.target.value}));
    }, [setPreset]);

    const blurDescription = useCallback((event: React.FocusEvent<HTMLTextAreaElement>) => {
        setPreset(prev => ({...prev, description: event.currentTarget.value.trim()}));
    }, [setPreset]);

    const backToPresets = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const updatePresetHandler = useCallback(async () => {
        let errors: Array<string | null> = [];
        setBusy(true);
        if (isPresetChanged) {
            errors.push(await savePreset() || null);
        }

        if (isPresetRelationsChanged) {
            savePresetRelations();

            if (rolesToDischarge.length) {
                errors.push(await dischargeRolesFromPreset({presetId, updateReason: reason}) || null);
            }

            if (grantsToDischarge.length) {
                errors.push(await dischargeGrantsFromPreset({presetId, updateReason: reason}) || null);
            }

            if (rolesToAdd.length) {
                errors.push(await assignRolesToPreset({presetId, updateReason: reason}) || null);
            }

            if (grantsToAdd.length) {
                errors.push(await assignGrantsToPreset({presetId, updateReason: reason}) || null);
            }
        }
        setBusy(false);

        errors = [...new Set(errors.filter(Boolean))] as string[];

        if (errors.length) {
            setFormErrorMessage(errors.join('\n'));
        } else {
            proceed();
        }
    }, [
        proceed,
        presetId,
        reason,
        isPresetChanged,
        savePreset,
        isPresetRelationsChanged,
        savePresetRelations,
        rolesToAdd,
        assignRolesToPreset,
        grantsToAdd,
        assignGrantsToPreset,
        rolesToDischarge,
        dischargeRolesFromPreset,
        grantsToDischarge,
        dischargeGrantsFromPreset,
    ]);

    const discardChanges = useCallback(() => {
        discardPreset();
        discardPresetRelations();
    }, [discardPreset, discardPresetRelations]);

    const addRelations = useCallback((existed: PresetRelationItem[]) => {
        setRelations(prev => [...new Set([...prev, ...existed])]);
    }, [setRelations]);

    const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setReason(e.target.value);
    };

    const handleReasonBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
        setReason(e.target.value.trim());
    };

    const openConfirmation = useCallback(() => {
        const errors = [validatePresetFields() || validatePresetRelations()];
        if (errors.filter(Boolean).length) {
            setErrorMessage(BASIC_VALIDATION_FAILED);
            return;
        }

        openSave();
    }, [openSave, validatePresetFields, validatePresetRelations, setErrorMessage]);

    return (
        <Skeleton shown={isPresetLoading || isPresetRelationsLoading} view={<Loading fast={true}/>}>
            <ServerResponseHandler
                serverError={presetLoadingError || presetRelationsLoadingError}
                accessRight={ACCESS_RIGHTS.MANAGE_ENTITLEMENTS as AccessRight}
                sectionName="Edit Preset">
                <ManageEntitlementsPage
                    title="Edit Preset"
                    buttonsBlock={(
                        <Group>
                            {hasAccess(ACCESS_RIGHTS.DELETE_ENTITLEMENT_PRESET) ? (
                                <Button variant="Type.SECONDARY" colorVariant="ERROR" onClick={openDelete}>
                                    Delete
                                </Button>
                            ) : null}
                            <Button variant="Type.TERTIARY" onClick={backToPresets}>
                                Cancel
                            </Button>
                            <Button
                                variant="Type.SECONDARY"
                                disabled={!isCurrentChunkChanged}
                                onClick={discardChanges}>
                                Discard Changes
                            </Button>
                            <Button
                                variant="Type.PRIMARY"
                                busy={busy}
                                disabled={!isCurrentChunkChanged}
                                onClick={openConfirmation}>
                                Save Changes
                            </Button>
                        </Group>
                    )}>
                    <div className="pd-l">
                        <FormSection headline="Preset Information">
                            <Group direction="v">
                                <CustomValidatedControl name={PRESET_NAME_CONTROL}>
                                    {error => (
                                        <FormInput
                                            value={preset.name}
                                            width={300}
                                            maxLength={64}
                                            label={<RequiredMark label="Preset Name"/>}
                                            placeholder="Preset Name"
                                            onBlur={changeName}
                                            valid={error ? false : undefined}
                                            errorDescription={error}
                                        />
                                    )}
                                </CustomValidatedControl>
                                <WidthItem width={610}>
                                    <Label>Description</Label>
                                    <Textarea
                                        maxLength={255}
                                        value={preset.description}
                                        onChange={changeDescription}
                                        onBlur={blurDescription}
                                    />
                                </WidthItem>
                            </Group>
                        </FormSection>
                        <PresetSuggestSection
                            relations={relations}
                            onAddRelations={addRelations}
                        />
                    </div>
                    <PresetEntitlementsTable
                        relations={relations}
                        onChange={setRelations}
                    />
                    <Dialog
                        shown={saveShown}
                        setShown={closeSave}
                        wrapperProps={{style: {width: 500}}}>
                        <DialogHeader onClose={closeSave}>
                            Save Changes
                        </DialogHeader>
                        <DialogMain style={{padding: 24}}>
                            {errorMessage ? <Message color="error">{errorMessage}</Message> : null}
                            <Label>Reason</Label>
                            <Textarea
                                value={reason}
                                resizable={false}
                                onChange={handleReasonChange}
                                onBlur={handleReasonBlur}
                                placeholder="Enter the change reason"
                                maxLength={128}
                            />
                        </DialogMain>
                        <DialogFooter>
                            <Group>
                                <Button
                                    disabled={!reason}
                                    busy={busy}
                                    variant="Type.PRIMARY"
                                    onClick={updatePresetHandler}>
                                    Save
                                </Button>
                                <Button variant="Type.SECONDARY" onClick={closeSave}>
                                    Cancel
                                </Button>
                            </Group>
                        </DialogFooter>
                    </Dialog>
                    <Dialog
                        shown={deleteShown}
                        setShown={closeDelete}
                        wrapperProps={{style: {width: REMOVE_PRESET_MODAL_WIDTH}}}>
                        <RemovePresetModal
                            presetId={presetId}
                            presetName={preset.name}
                            onClose={closeDelete}
                            onDone={() => {
                                closeDelete();
                                setSuccessMessage('Preset removed successfully');
                                navigate('/entitlements/presets');
                            }}
                        />
                    </Dialog>
                </ManageEntitlementsPage>
            </ServerResponseHandler>
        </Skeleton>
    );
};

export const ValidateEditPreset = () => (
    <ValidationContextProvider>
        <EditPreset/>
    </ValidationContextProvider>
);
